import { API_CONFIG } from '@/config/api.config';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, CheckCircle, ArrowRight, Loader2, Users, Star, Award, Compass } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext'; 
import { auth } from '@/lib/firebase';

type TierKey = 'explorer' | 'pro' | 'elite';

const TIERS: Record<TierKey, {
  priceId: string;
  price: number;
  regularPrice: number;
  name: { en: string; fr: string };
}> = {
  explorer: {
    priceId: 'price_1TcynCCkUJGKtqQAzAYqUe1n',
    price: 399,
    regularPrice: 599,
    name: { en: 'Explorer', fr: 'Explorer' },
  },
  pro: {
    priceId: 'price_1TcymACkUJGKtqQA13iBWe5k',
    price: 720,
    regularPrice: 1080,
    name: { en: 'Pro', fr: 'Pro' },
  },
  elite: {
    priceId: 'price_1TcynzCkUJGKtqQAJggREXWd',
    price: 1440,
    regularPrice: 2160,
    name: { en: 'Elite', fr: 'Elite' },
  },
};

const ConsultantPayment: React.FC = () => {
  const [loadingTier, setLoadingTier] = useState<TierKey | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user } = useAuth(); // NOUVEAU — corrige le ReferenceError qui existait avant

  // NOUVEAU — si l'utilisateur revient ici après vérification Didit (ou
  // simplement authentifié) et qu'un tier avait été choisi avant
  // inscription, on le relance automatiquement au chargement.
  useEffect(() => {
    if (!user) return;
    try {
      const saved = localStorage.getItem('selectedConsultantPlan');
      if (saved) {
        const { tier } = JSON.parse(saved);
        if (tier && TIERS[tier as TierKey]) {
          localStorage.removeItem('selectedConsultantPlan');
          handleSubscription(tier as TierKey);
        }
      }
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSubscription = async (tier: TierKey) => {
    if (!user) {
      localStorage.setItem('selectedConsultantPlan', JSON.stringify({ tier }));
      navigate('/register/consultant');
      return;
    }

    setLoadingTier(tier);
    try {
      const idToken = await auth.currentUser?.getIdToken(true);
if (!idToken) throw new Error('Authentication token unavailable. Please sign in again.');
      const res = await fetch(`${API_CONFIG.baseURL}/payments/consultant-checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          tier: `consultant_${tier}`,
          origin: window.location.origin,
          successPath: '/payment-success?role=consultant',
          cancelPath: '/consultant/payment',
        }),
      });

      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error(errText || `Checkout failed (${res.status})`);
      }

      const data = await res.json();
      if (!data?.url) throw new Error('No checkout URL returned');
      window.location.href = data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: language === 'fr' ? 'Erreur' : 'Error',
        description: language === 'fr'
          ? 'Une erreur est survenue lors de la création du paiement.'
          : 'An error occurred while creating the payment.',
        variant: 'destructive',
      });
    } finally {
      setLoadingTier(null);
    }
  };

  const featuresByTier: Record<TierKey, { en: string[]; fr: string[] }> = {
    explorer: {
      en: [
        'Listed in consultant directory',
        'Access to Deal Monitor',
        'Secure messaging',
        'Basic calendar',
        'Up to 5 active mandates',
      ],
      fr: [
        'Répertoire des consultants',
        'Accès au Deal Monitor',
        'Messagerie sécurisée',
        'Calendrier de base',
        "Jusqu'à 5 mandats actifs",
      ],
    },
    pro: {
      en: [
        'All Explorer benefits',
        'Unlimited mandates',
        'Profile highlighted in directory',
        'Video conferencing',
        'Shared Data Room',
        'Basic analytics (profile views)',
      ],
      fr: [
        'Tous les avantages Explorer',
        'Mandats illimités',
        'Profil mis en avant dans le répertoire',
        'Visioconférence',
        'Data Room partagée',
        'Analytics basiques (vues profil)',
      ],
    },
    elite: {
      en: [
        'All Pro benefits',
        'Verified & Certified badge',
        'Priority #1 in directory',
        'Advanced analytics',
        '1 Market Study included/year',
        'Featured on relevant deal listings',
        'Referral priority for new mandates',
      ],
      fr: [
        'Tous les avantages Pro',
        'Badge Vérifié & Certifié',
        'Priorité #1 dans le répertoire',
        'Analytics avancées',
        '1 Market Study inclus/an',
        'Mise en avant sur les listings pertinents',
        'Priorité sur les nouveaux mandats référés',
      ],
    },
  };

  const tierMeta: Record<TierKey, {
    icon: React.ReactNode;
    badge?: { en: string; fr: string };
    highlight: boolean;
  }> = {
    explorer: {
      icon: <Compass className="w-6 h-6 text-white" />,
      highlight: false,
    },
    pro: {
      icon: <Star className="w-6 h-6 text-[#CEAB65]" />,
      badge: { en: 'Popular', fr: 'Populaire' },
      highlight: true,
    },
    elite: {
      icon: <Award className="w-6 h-6 text-[#CEAB65]" />,
      badge: { en: 'Best Value', fr: 'Meilleure Valeur' },
      highlight: true,
    },
  };

  const renderCard = (tierKey: TierKey) => {
    const tier = TIERS[tierKey];
    const meta = tierMeta[tierKey];
    const features = featuresByTier[tierKey][language as 'en' | 'fr'] || featuresByTier[tierKey].en;
    const savings = Math.round(((tier.regularPrice - tier.price) / tier.regularPrice) * 100);
    const isLoading = loadingTier === tierKey;

    return (
      <div
        key={tierKey}
        className={`bg-background shadow-2xl rounded-xl overflow-hidden flex flex-col relative ${
          meta.highlight ? 'ring-2 ring-[#CEAB65]' : ''
        }`}
      >
        {meta.badge && (
          <div className="absolute top-0 right-0 bg-[#CEAB65] text-[#1D314D] px-4 py-1 text-sm font-bold rounded-bl-lg z-10">
            {meta.badge[language as 'en' | 'fr']}
          </div>
        )}

        <div className="bg-gradient-to-r from-[#1D314D] to-[#2E4663] p-6 text-center">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
            {meta.icon}
          </div>
          <h2 className="text-2xl font-bold text-white">
            {tier.name[language as 'en' | 'fr']}
          </h2>
          <p className="text-white/70 text-sm">
            {language === 'fr' ? 'Abonnement annuel' : 'Annual subscription'}
          </p>
        </div>

        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center justify-center gap-2 mb-1">
            <span className="text-sm text-slate-500 line-through">
              ${tier.regularPrice.toLocaleString()}
            </span>
            <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
              -{savings}%
            </span>
          </div>
          <div className="flex items-baseline justify-center">
            <span className="text-4xl font-extrabold text-[#CEAB65]">
              ${tier.price.toLocaleString()}
            </span>
            <span className="ml-2 text-lg font-medium text-slate-500">
              / {language === 'fr' ? 'an' : 'year'}
            </span>
          </div>
        </div>

        <div className="p-6 flex-grow">
          <div className="space-y-3">
            {features.map((f, i) => (
              <div key={i} className="flex items-start">
                <CheckCircle className="w-4 h-4 text-[#CEAB65] mt-0.5 flex-shrink-0" />
                <span className="ml-2 text-sm text-slate-700">{f}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 pt-0 mt-auto">
          <button
            onClick={() => handleSubscription(tierKey)}
            disabled={isLoading}
            className={`w-full flex items-center justify-center px-6 py-3 text-base font-medium rounded-lg shadow-lg transition duration-300 ${
              isLoading
                ? 'bg-[#CEAB65]/60 text-[#1D314D] cursor-not-allowed'
                : 'bg-[#CEAB65] text-[#1D314D] hover:bg-[#CEAB65]/90'
            }`}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {language === 'fr' ? 'Traitement…' : 'Processing…'}
              </>
            ) : (
              <>
                {user
                  ? (language === 'fr' ? 'Procéder au paiement' : 'Proceed to payment')
                  : (language === 'fr' ? "S'abonner" : 'Subscribe')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </>
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1D314D] via-[#2E4663] to-[#1D314D] p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-3">
            {language === 'fr' ? 'Programme Consultant' : 'Consultant Program'}
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto">
            {language === 'fr'
              ? "Choisissez le plan qui correspond à votre activité et rejoignez un réseau exclusif d'experts en M&A."
              : 'Pick the plan that fits your practice and join an exclusive network of M&A experts.'}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {(['explorer', 'pro', 'elite'] as TierKey[]).map(renderCard)}
        </div>

        {/* Info section */}
        <div className="bg-background rounded-xl shadow-2xl p-6 sm:p-10 max-w-4xl mx-auto">
          <div className="flex items-center mb-4">
            <Users className="w-6 h-6 text-[#CEAB65] mr-2" />
            <h2 className="text-2xl font-bold text-[#1D314D]">
              {language === 'fr' ? 'Ce qui est inclus' : "What's included"}
            </h2>
          </div>
          <p className="text-slate-700 mb-4">
            {language === 'fr'
              ? "Tous les plans Consultant incluent l'inscription au répertoire, l'accès au Deal Monitor, une boîte de dépôt de documents sécurisée, le calendrier intégré et une MailBox sécurisée pour la communication client. Les plans supérieurs ajoutent visibilité, analytics et bénéfices premium."
              : 'All Consultant plans include directory listing, Deal Monitor access, a secure document drop box, integrated calendar, and a secure MailBox for client communication. Higher tiers add visibility, analytics, and premium perks.'}
          </p>
          <div className="text-center text-sm text-slate-500 mt-6">
            <CreditCard className="inline w-4 h-4 mr-1 mb-0.5" />
            {language === 'fr'
              ? 'Paiement sécurisé · Renouvellement annuel automatique.'
              : 'Secure payment · Automatic annual renewal.'}
          </div>
          <div className="text-center mt-4">
            <button
              onClick={() => navigate('/contact')}
              className="text-[#1D314D] underline hover:text-[#CEAB65] text-sm"
            >
              {language === 'fr' ? "Une question ? Contactez l'équipe" : 'Have a question? Contact the team'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsultantPayment;